import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScrewdriverWrench, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { customerEquipmentService } from '@/services/customer-equipment.service';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { locationParamFor, useCustomerScopeStore } from '@/stores/customer-scope.store';
import { CustomerLocationContext } from '@/components/customer-portal/CustomerLocationContext';
import { customerEquipmentDetailPath } from '@/routes/customer-portal-routes';
import { CustomerSearchBar } from '@/components/customer-portal/CustomerSearchBar';
import { CustomerPagination } from '@/components/customer-portal/CustomerPagination';
import { CustomerPageHeader } from '@/components/customer-portal/CustomerPageHeader';
import { CustomerEmptyState } from '@/components/customer-portal/CustomerEmptyState';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerStatusBadge } from '@/components/customer-portal/CustomerStatusBadge';
import type { CustomerBadgeTone } from '@/components/customer-portal/CustomerStatusBadge';
import { CustomerSkeletonList } from '@/components/customer-portal/skeletons/CustomerSkeleton';
import { useDebounce } from '@/hooks/use-debounce';

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  RETIRED: 'Retired',
};

const STATUS_TONE: Record<string, CustomerBadgeTone> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  RETIRED: 'warning',
};

export function CustomerEquipmentListPage() {
  const user = useCustomerAuthStore((s) => s.user);
  const selectedLocation = useCustomerScopeStore((s) => s.selectedLocationId);
  // D-130 — LOCATION contacts send nothing; the server scopes them already.
  const locationId = locationParamFor(user?.locationScope, selectedLocation);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['customer-portal', 'equipment', page, debouncedSearch, locationId ?? 'all'],
    queryFn: () =>
      customerEquipmentService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        locationId,
      }),
  });

  return (
    <div className="space-y-4 sm:space-y-5">
      <CustomerPageHeader
        title="Equipment"
        subtitle="Equipment registered to your account"
        eyebrow={data ? `${data.total} record${data.total === 1 ? '' : 's'}` : undefined}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <CustomerSearchBar
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search by name, asset or serial number…"
        />
        <CustomerLocationContext className="sm:w-52" />
      </div>

      {isLoading && <CustomerSkeletonList rows={6} />}

      {isError && !isLoading && (
        <CustomerErrorState
          message="We couldn't load your equipment."
          onRetry={() => void refetch()}
        />
      )}

      {data && !isLoading && !isError && (
        <>
          {data.items.length === 0 ? (
            <div className="rounded-customer border border-dashed border-customer-border bg-customer-surface">
              <CustomerEmptyState
                icon={faScrewdriverWrench}
                tone="neutral"
                title="No equipment found"
                description={
                  search
                    ? 'No equipment matches your search.'
                    : 'No equipment is registered to your account yet.'
                }
                className="py-14"
              />
            </div>
          ) : (
            <div className="customer-fade-in overflow-hidden rounded-customer border border-customer-border bg-customer-surface shadow-customer-sm">
              <ul className="divide-y divide-customer-border">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => navigate(customerEquipmentDetailPath(item.id))}
                      className="customer-pressable flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-customer-ring sm:px-5"
                    >
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-customer-sm bg-customer-primary/10 text-customer-primary-text"
                        aria-hidden="true"
                      >
                        <FontAwesomeIcon icon={faScrewdriverWrench} className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <p className="truncate text-sm font-semibold text-customer-text">
                            {item.equipmentName}
                          </p>
                          <CustomerStatusBadge
                            label={STATUS_LABEL[item.status] ?? item.status}
                            tone={STATUS_TONE[item.status] ?? 'neutral'}
                          />
                        </div>
                        <p className="mt-0.5 truncate text-xs text-customer-text-secondary">
                          {item.manufacturerName} · {item.modelNumber}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-customer-text-secondary">
                          Asset {item.assetNumber} · Serial {item.serialNumber}
                        </p>
                      </div>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="h-3.5 w-3.5 shrink-0 text-customer-text-secondary"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CustomerPagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
